import { graphql, fetchQuery, type Environment } from 'react-relay';
import { OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import type {
	EndCursor,
	OptionResponse,
	AvatarOption,
	SelectOption,
	SearchTemplate,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type {
	groupJqlBuilderBasicFieldValuesQuery,
	JiraJqlViewContext,
} from '@atlassian/jira-relay/src/__generated__/groupJqlBuilderBasicFieldValuesQuery.graphql';
import type { groupJqlBuilderBasicInitQuery } from '@atlassian/jira-relay/src/__generated__/groupJqlBuilderBasicInitQuery.graphql';
import type { FormatMessage, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { PropsResolver } from '../../../../../../common/types.tsx';
import {
	generateOptions,
	generateErrorOptions,
	fireJqlBuilderBasicErrorAnalytics,
} from '../../../../../../common/utils.tsx';
import messages from './messages.tsx';

type Params = {
	fieldId: string;
	operandDisplayValues?: string[];
	formatMessage: FormatMessage;
	jql?: string;
	environment: Environment;
	cloudId: CloudId;
	searchTemplate: SearchTemplate;
	viewContext: JiraJqlViewContext | null;
};

type GetSuggestedParams = {
	environment: Environment;
	cloudId: CloudId;
	formatMessage: FormatMessage;
};

type GetFieldValueParams = {
	environment: Environment;
	cloudId: CloudId;
	lowerCaseFieldId: string;
	jql?: string;
	search: string;
	endCursor: EndCursor;
	viewContext: JiraJqlViewContext | null;
};

const getSuggestedOptions = async ({
	environment,
	cloudId,
	formatMessage,
}: GetSuggestedParams): Promise<OptionResponse> => {
	const data = await fetchQuery<groupJqlBuilderBasicInitQuery>(
		environment,
		graphql`
			query groupJqlBuilderBasicInitQuery($cloudId: ID!) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						suggestedGroups(first: 10, context: GROUP) {
							edges {
								node {
									jqlTerm
									displayName
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();

	if (!data?.jira?.jqlBuilder?.suggestedGroups?.edges) {
		return generateOptions([]);
	}

	const groups =
		data?.jira?.jqlBuilder?.suggestedGroups?.edges?.reduce<Array<AvatarOption>>((options, edge) => {
			if (!edge || !edge.node) {
				return options;
			}
			const { node } = edge;
			options.push({
				label: node.displayName,
				value: node.jqlTerm,
				optionType: 'avatar',
				isGroup: true,
				itemType: OPERAND_TYPE_VALUE,
			});
			return options;
		}, []) || [];

	const options: SelectOption[] = [
		{
			label: formatMessage(messages.refinementOptionSuggestedGroups),
			optionType: 'group',
			options: groups,
		},
	];
	return generateOptions(options);
};
const getFieldValueOptions = async ({
	environment,
	cloudId,
	lowerCaseFieldId,
	jql,
	search,
	endCursor,
	viewContext,
}: GetFieldValueParams): Promise<OptionResponse> => {
	const after = endCursor;

	const data = await fetchQuery<groupJqlBuilderBasicFieldValuesQuery>(
		environment,
		graphql`
			query groupJqlBuilderBasicFieldValuesQuery(
				$cloudId: ID!
				$first: Int = 10
				$jqlTerm: String!
				$jql: String!
				$searchString: String!
				$after: String
				$viewContext: JiraJqlViewContext
			) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						fieldValues(
							first: $first
							jqlTerm: $jqlTerm
							jqlContext: $jql
							searchString: $searchString
							after: $after
							viewContext: $viewContext
						) {
							totalCount
							pageInfo {
								endCursor
							}
							edges {
								node {
									jqlTerm
									displayName
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			first: 10,
			jqlTerm: lowerCaseFieldId,
			jql: jql || '',
			searchString: search,
			after,
			viewContext,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();
	if (!data?.jira?.jqlBuilder?.fieldValues?.edges) {
		return generateOptions([]);
	}

	const { edges: fieldValues, totalCount, pageInfo } = data.jira.jqlBuilder.fieldValues;
	return generateOptions(
		fieldValues.reduce<Array<AvatarOption>>((options, edge) => {
			if (!edge || !edge.node) {
				return options;
			}
			const { node } = edge;
			options.push({
				label: node.displayName,
				value: node.jqlTerm,
				optionType: 'avatar',
				isGroup: true,
				itemType: OPERAND_TYPE_VALUE,
			});
			return options;
		}, []),
		totalCount,
		pageInfo?.endCursor || undefined,
	);
};

export const getGroupProps = ({
	fieldId,
	operandDisplayValues,
	formatMessage,
	jql,
	environment,
	cloudId,
	searchTemplate,
	viewContext,
}: Params): PropsResolver => ({
	enableFullList: true,
	operandDisplayValues,
	loadOptions: async (search, renderInitialValues, endCursor) => {
		try {
			if (search.length === 0 && renderInitialValues) {
				return await getSuggestedOptions({
					environment,
					cloudId,
					formatMessage,
				});
			}
			return await getFieldValueOptions({
				environment,
				cloudId,
				lowerCaseFieldId: fieldId.toLowerCase(),
				jql,
				search,
				endCursor,
				viewContext,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireJqlBuilderBasicErrorAnalytics('groupJqlBuilderBasicFieldValues', searchTemplate, error);
			return generateErrorOptions(error);
		}
	},
});
